<template>
  <div class="webinar__screen" @contextmenu="onContextMenu">
    <header class="webinar__header">
      <div class="screen-wrapper">
        <a :href="mainPage" class="webinar__header__login">
          <img v-if="logo" :src="`${baseURI}uploads/160-40/${logo}`"
               :srcset="`${baseURI}uploads/320-80/${logo} 2x`" alt=""/>
        </a>
        <div class="webinar__header__links">
          <a v-for="(link, index) in webinar.links" :key="index" target="_blank"
             class="webinar__header__link"
             :href="addUtm(link.href)">
            {{ link.label }}
            <icon-arrow-alt/>
          </a>
        </div>
      </div>
    </header>
    <div class="webinar-wrapper webinar__page__wrapper">
      <div class="webinar__title">
        <div class="webinar__title__text">{{ webinar.title }}</div>
        <div class="webinar__reload" @click="reload"
             :title="t('public.reload')">
          <icon-reload/>
        </div>
      </div>
      <div class="webinar__body">
        <div class="webinar__video-wrapper">
          <div class="webinar__video">
            <div class="webinar__online">
              <icon-eye/>&nbsp;{{ users.length }}
            </div>
            <div class="countdown" v-if="schedule.left < 0">
              <div class="countdown__title">{{ t('public.startAfter') }}</div>
              <div class="countdown__time">{{ countdown }}</div>
            </div>
            <template v-else-if="showVideo">
              <you-tube :src="webinar.youtube" class="youtube-video-player"
                        ref="youtubeIframe" :vars="playerVars"
                        @ready="initYoutube" @state-change="stateChange"/>
              <div class="webinar__loading-video" v-if="loadingVideo"
                   @click="startOnClick">
                <icon-play class="webinar__loading-video__play"
                           v-if="!youtubePlay"/>
                <loading/>
              </div>
            </template>
            <div class="translation-finished" v-else>
              {{ t('public.finished') }}
              <Button class="translation-finished__button"
                      v-if="webinar.archived" :href="archivedLink" currentTab>
                {{ t('public.recording') }}
              </Button>
            </div>
            <video-slider v-if="archived" :timing="videoTiming"
                          :duration="videoDuration" @click.stop
                          @change="setTiming"/>
          </div>
        </div>
        <chat :schedule="schedule.id" :users="users"
              :started="schedule.left >= 0"
              :videoTiming="videoTiming"
              :webinarId="webinar.id"
              :disabled="archived || schedule.finished || schedule.left < 0"/>
      </div>
      <div class="webinar__description" v-html="webinar.description"
           v-if="schedule.left < 0"></div>
      <template v-else>
        <admin-buttons v-if="administration" :schedule="schedule.id"
                       :webinar="webinar.id" :videoTiming="videoTiming"/>
        <buttons v-else :schedule="schedule.id" :webinar="webinar.id"
                 :videoTiming="videoTiming"/>
        <div class="finish-button" @click="finishTranslation"
             v-if="administration">
          <template v-if="loadingFinishing">{{ t('public.wait') }}</template>
          <template v-else>{{ t('public.finish') }}</template>
        </div>
      </template>
    </div>
    <footer class="webinar__footer">
      <div class="screen-wrapper">
        <div class="webinar__footer__left" v-html="footer.left"></div>
        <div class="webinar__footer__right" v-html="footer.right"></div>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, PropType, ref, watch,
} from 'vue'
import axios from 'axios'
import { Webinar, WebinarSchedule } from '@/types/webinar.d'
import YouTube from 'vue3-youtube'
import AdminButtons from '@/vue/public/AdminButtons.vue'
import getCountdown from '@/scripts/countdown'
import IconArrowAlt from '@/vue/icons/IconArrowAlt.vue'
import IconEye from '@/vue/icons/IconEye.vue'
import { useStore } from 'vuex'
import { key } from '@/vue/store'
import Loading from '@/vue/public/Loading.vue'
import IconReload from '@/vue/icons/IconReload.vue'
import { useI18n } from 'vue-i18n'
import Timeout from '@/types/timeout.d'
import IconPlay from '@/vue/icons/IconPlay.vue'
import VideoSlider from '@/vue/public/VideoSlider.vue'
import Button from '@/vue/public/Button.vue'
import Buttons from './Buttons.vue'
import Chat from './Chat.vue'

const playerVars = {
  controls: 0,
  disablekb: 1,
  fs: 0,
  iv_load_policy: 3,
  modestbranding: 1,
  rel: 0,
  showinfo: 0,
  origin: window.location.host,
  playsinline: 1,
  autoplay: 0,
}

declare global {
  interface Window {
    BASE_URI: string
    footerText: string
    webinarsLogo: string
  }
}

export default defineComponent({
  name: 'Webinar',
  props: {
    webinar: {
      type: Object as PropType<Webinar>,
      required: true,
    },
    schedule: {
      type: Object as PropType<WebinarSchedule>,
      required: true,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Button,
    VideoSlider,
    IconPlay,
    IconReload,
    Loading,
    Buttons,
    Chat,
    YouTube,
    AdminButtons,
    IconArrowAlt,
    IconEye,
  },
  setup(props) {
    const { t } = useI18n()

    const youtubeIframe = ref<typeof YouTube.methods>()
    const dueDate = ref(Date.now())
    const now = ref(Date.now())
    const videoDuration = ref(0)
    const countdown = computed(() => getCountdown(Math.round((dueDate.value - now.value) / 1000), t('public.daysLetter')))
    const loadingVideo = ref(true)

    let youtubeReady = false
    const youtubePlay = ref(false)

    const updateDueDate = () => {
      dueDate.value = Date.now() - (props.schedule.left * 1000)
    }
    updateDueDate()
    watch(() => props.schedule, updateDueDate)

    const timeInterval = setInterval(() => {
      now.value = Date.now()
    }, 100)
    onBeforeUnmount(() => clearInterval(timeInterval))

    let playerStateInterval: Timeout
    const initYoutube = () => {
      youtubeReady = true
      if (!props.schedule.finished && youtubeIframe.value && !props.archived) {
        playerStateInterval = setInterval(() => {
          if (youtubeIframe.value && youtubeIframe.value.getPlayerState() !== 1) {
            if (!props.schedule.finished && youtubePlay.value) {
              youtubeIframe.value.playVideo()
            }
          } else {
            clearInterval(playerStateInterval)
          }
        }, 100)
      }
      videoDuration.value = youtubeIframe.value?.getDuration() || 0
    }
    onBeforeUnmount(() => clearInterval(playerStateInterval))

    const store = useStore(key)
    const administration = computed(() => store.state.userAdmin && !props.schedule.endAt)

    let loadingTimeout: Timeout
    const stateChange = ({ data }: { data: number }) => {
      clearTimeout(loadingTimeout)
      if (youtubeIframe.value) {
        if (data === 1) {
          loadingTimeout = setTimeout(() => {
            loadingVideo.value = false
          }, 4000)
          youtubePlay.value = true
        } else {
          loadingVideo.value = true
          if (data === 2) {
            if (props.archived) {
              youtubePlay.value = false
            } else {
              youtubeIframe.value.seekTo(props.schedule.left > 0 ? props.schedule.left : 0, true)
              youtubeIframe.value.playVideo()
            }
          }
        }
      }
    }

    const videoTiming = ref(0)
    if (props.archived) {
      const vtt = setInterval(() => {
        if (youtubeIframe.value && youtubePlay.value) {
          videoTiming.value = Math.floor(youtubeIframe.value.getCurrentTime())
        }
      }, 1000)
      onBeforeUnmount(() => {
        clearInterval(vtt)
      })
    }

    const users = ref([])
    let usersTimeout: Timeout
    const getUsers = async () => {
      clearTimeout(usersTimeout)
      users.value = await axios.get(`/schedule/${props.schedule.id}/users`, props.archived ? {
        params: {
          webinar: props.webinar.id,
          timing: videoTiming.value,
        },
      } : {}).then(({ data }) => data).catch(() => [])
      usersTimeout = setTimeout(getUsers, 3000)
    }
    getUsers()

    const loadingFinishing = ref(false)
    const finishTranslation = async () => {
      // eslint-disable-next-line no-alert
      if (store.state.userAdmin && !props.schedule.endAt && !loadingFinishing.value && confirm(t('public.confirmFinish'))) {
        loadingFinishing.value = true
        await axios.post(`/webinars/${props.schedule.id}/finish`)
      }
    }

    const showVideo = computed(() => !props.schedule.finished
      && !(videoDuration.value && props.schedule.left > videoDuration.value))

    const reload = () => document.location.reload()

    const utm = computed(() => store.state.userUtm)
    const addUtm = (href: string) => {
      let l: string = href
      if (utm.value) {
        l += l.includes('?') ? '&' : '?'
        l += utm.value.replace(/^\?/, '')
      }
      return l
    }

    const onContextMenu = (e: MouseEvent) => {
      if (!store.state.userAdmin) {
        e.preventDefault()
      }
    }

    const startOnClick = () => {
      if (youtubeReady && youtubeIframe.value) {
        if (youtubePlay.value) {
          if (props.archived) {
            youtubeIframe.value.pauseVideo()
            youtubePlay.value = false
          }
        } else {
          if (!props.archived) {
            youtubeIframe.value.seekTo(props.schedule.left > 0 ? props.schedule.left : 0, true)
          }
          youtubeIframe.value.playVideo()
          youtubePlay.value = true
        }
      }
    }

    const setTiming = (time: number) => {
      if (props.archived) {
        youtubeIframe.value?.seekTo(time, true)
        videoTiming.value = time
      }
    }

    const archivedLink = `${document.location.pathname}?archived=1`

    return {
      mainPage: window.BASE_URI,
      logo: window.webinarsLogo,
      footer: window.footerText,
      countdown,
      initYoutube,
      youtubeIframe,
      playerVars,
      stateChange,
      users,
      administration,
      finishTranslation,
      loadingFinishing,
      showVideo,
      loadingVideo,
      reload,
      t,
      addUtm,
      onContextMenu,
      startOnClick,
      youtubePlay,
      videoTiming,
      videoDuration,
      setTiming,
      baseURI: window.BASE_URI,
      archivedLink,
    }
  },
})
</script>

<style lang="stylus">
.webinar__screen
  .youtube-video-player
    width 100% !important
    height 100% !important

  .translation-finished
    color var(--white-text-color)
    text-align center
    font-size 1.36em
    absolute left top
    size 100%
    z-index 20
    display flex
    flex-direction column
    align-items center
    justify-content center
    &__button
      margin 20px 0 0

  .countdown
    color var(--white-text-color)
    text-align center
    absolute left top
    size 100%
    z-index 2
    display flex
    flex-direction column
    align-items center
    justify-content center

    &__title
      font-size 1.36em
      margin-bottom 10px

    &__time
      font-size 2.86em

    .translation-iframe
      absolute top left
      size 100%

  .finish-button
    background var(--button-color)
    color var(--button-text-color)
    text-decoration none
    text-transform uppercase
    white-space nowrap
    text-overflow ellipsis
    width 250px
    max-width 100%
    font-weight bold
    padding 8px 15px
    border-radius 3px
    margin 30px 0 0 auto
    transition .2s
    cursor pointer
    text-align center

.webinar
  &__body
    display flex
    justify-content space-between
    margin-bottom 20px
    width 100%

  &__video-wrapper
    width calc(100% + 40px)
    margin 0 -20px

  &__video
    background var(--dark-bg-color)
    position relative
    width 100%
    padding-top 56.25%

    &::after
      content ''
      absolute top left
      size 100%
      z-index 10

    .youtube-video-player
      position absolute !important
      top 0
      left 0
      size 100%

  &__online
    color #fff
    font-size 1rem
    absolute top 10px left 10px
    z-index 5
    background rgba(0, 0, 0, .5)
    border-radius 5px
    padding 0 5px

  &__loading-video
    position absolute
    top 0
    left 0
    width 100%
    height 100%
    z-index 20
    display flex
    align-items center
    justify-content center
    font-size 2em
    background var(--dark-bg-color)
    color var(--button-color)

    &__play
      absolute left 50% top 50%
      font-size 56px
      margin-left -25px
      margin-top -28px

      & + .loading
        display none

  &__chat
    width 330px

  &__buttons
    display flex
    flex-wrap wrap

  &__page__wrapper
    position relative
    overflow hidden

  &__title
    font-size 1.86em
    font-weight bold
    margin-bottom 20px
    overflow hidden
    width 100%
    align-items center
    display flex

    &__text
      text-align center
      width 100%

  &__reload
    background var(--button-color)
    color var(--button-text-color)
    cursor pointer
    text-decoration none
    text-transform uppercase
    white-space nowrap
    text-overflow ellipsis
    max-width 100%
    font-weight bold
    padding 8px 15px
    border-radius 3px

  &__description
    h1
    h2
    h3
    h4
    h5
    p
    ul
    ol
    blockquote
      color inherit

    h1
    h2
    h3
    h4
    h5
    h6
      font-weight 500
      line-height 1.2
      margin-bottom .5rem

    h1
      font-size 2.25rem

    h2
      font-size 1.8rem

    h3
      font-size 1.575rem

    h4
      font-size 1.35rem

    h5
      font-size 1.125rem

    h6
      font-size .9rem

    p
    ul
    ol
    blockquote
      margin-bottom 1rem

      &:last-child
        margin-bottom 0

    ul
      list-style circle

    ol
      list-style decimal

    ul
    ol
      padding-left 20px

@media screen and (min-width: 940px)
  .webinar
    &__video-wrapper
      width calc(100% - 350px)
      margin 0

    &__video
      min-height 400px

    &__description
      padding-right 350px

    &__loading-video
      cursor pointer
</style>
